import { useFormikContext } from 'formik'

import type { OrchestratedOfferingListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import {
  ClientTimeZoneDateTime,
  DateTimeFormatType,
  FormattedMessage,
  parseOptionalISODate,
} from '@b-stock/fe-core/bstock-react'
import { CommonStrings } from '@b-stock/fe-core/locale'

import { type MakeAnOfferFormValues } from '../../offering/SimpleOfferActionModule/MakeAnOfferFormProvider'
import { SectionContent } from '../ui'
import {
  OfferStatusAlert,
  type OfferStatusAlertVariant,
} from './OfferStatusAlert'
import { useOfferAmount } from './useOfferAmount'
import type { OfferDecisionState } from './useOfferDecisionState'

const ns = 'Listing.actionColumn.actionCard.offer'

// A submitted offer reads as pending; a canceled listing borrows the declined
// styling but reports the listing, not the offer.
const ALERTS: Partial<
  Record<
    OfferDecisionState,
    { copyKey: string; variant: OfferStatusAlertVariant }
  >
> = {
  accepted: { copyKey: 'accepted', variant: 'accepted' },
  canceled: { copyKey: 'canceled', variant: 'declined' },
  declined: { copyKey: 'declined', variant: 'declined' },
  pending: { copyKey: 'pending', variant: 'pending' },
  submitted: { copyKey: 'pending', variant: 'pending' },
}

/**
 * Replaces the make-an-offer flow once there is an offer to report on, or the
 * window has closed: a status alert over the explanatory copy for the state. An
 * expired listing gets the copy alone — there is no offer of ours to report —
 * and a canceled one just the alert.
 */
export const SimpleOfferStatus = ({
  listing,
  state,
}: {
  listing: OrchestratedOfferingListingDetails
  state: Exclude<OfferDecisionState, 'live'>
}) => {
  const { offering, offers, seller } = listing
  const { values } = useFormikContext<MakeAnOfferFormValues>()
  const { renderOfferAmount } = useOfferAmount(listing)

  const alert = ALERTS[state]
  const isAwaitingDecision = state === 'pending' || state === 'submitted'
  const isCanceled = state === 'canceled'
  const sellerName = seller.account.displayName || (
    <CommonStrings.NotAvailableAbbr.Component />
  )

  return (
    <>
      {alert && (
        <OfferStatusAlert
          amount={
            isCanceled
              ? undefined
              : renderOfferAmount(values.currentOfferAmount)
          }
          copyKey={alert.copyKey}
          date={
            isAwaitingDecision
              ? parseOptionalISODate(offers?.at(-1)?.updatedAt)
              : null
          }
          isDecisionWindowNotOpen={state === 'submitted'}
          variant={alert.variant}
        />
      )}
      {!isCanceled && (
        <SectionContent>
          <FormattedMessage
            id={`${ns}.status.${state}.body`}
            values={{
              date: (
                <ClientTimeZoneDateTime
                  dateTime={parseOptionalISODate(offering?.offerEndTime)}
                  format={DateTimeFormatType.SHORT}
                />
              ),
              sellerName,
            }}
          />
        </SectionContent>
      )}
    </>
  )
}
